import styles from './ConflictDialog.module.scss'

import type { GtConflictReason } from '@/api/internal'
import Button from '@/components/Button'
import DialogWrapper from '@/components/Dialog'

export interface ConflictDialogProps {
  isOpen: boolean
  reason: GtConflictReason
  currentVersion: number | null
  onReloadTheirs: () => void
  onOverwriteMine: () => void
  onCancel: () => void
}

const TITLE: Record<GtConflictReason, string> = {
  stale_version: 'Someone else saved a newer version',
  conflict: 'This review has changed',
  not_complete: 'This review is not complete',
  generation_conflict: 'A newer version was finalized',
  unchanged: 'Nothing to save',
  prefill_mismatch: 'The source transcript changed',
  source_unavailable: 'The source transcript is unavailable',
  legacy_schema: 'This review needs to be reloaded',
}

const MESSAGE: Record<GtConflictReason, (currentVersion: number | null) => string> = {
  stale_version: (v) =>
    v != null
      ? `Version ${v} was saved by someone else while you were editing. Reload their version to see the latest changes, or overwrite it with yours.`
      : 'This review was finalized by someone else while you were editing. Reload to continue.',
  conflict: () => 'This review changed underneath your edits. Reload to see the latest version before continuing.',
  not_complete: () => 'This review has open blockers and cannot be saved as complete yet.',
  generation_conflict: () =>
    'Someone else finalized a newer generation of this transcript. Reload to pick up the latest version.',
  unchanged: () => 'Nothing has changed since the last save.',
  prefill_mismatch: () =>
    'The underlying transcript was regenerated since this review started. Reload to continue with the current source.',
  source_unavailable: () => 'The source transcript could not be verified. Saving is paused. Reload to check again.',
  legacy_schema: () => 'This review was created by an older version of this tool and needs to be reloaded.',
}

const canOverwrite = (reason: GtConflictReason): boolean => reason === 'stale_version' || reason === 'conflict'

export const ConflictDialog = ({
  isOpen,
  reason,
  currentVersion,
  onReloadTheirs,
  onOverwriteMine,
  onCancel,
}: ConflictDialogProps) => (
  <DialogWrapper isOpen={isOpen} title={TITLE[reason]} close={onCancel}>
    <div className={styles.body}>
      <p className={styles.message}>{MESSAGE[reason](currentVersion)}</p>
      <div className={styles.actions}>
        <Button title="Cancel" type="link" onClick={onCancel} />
        <Button title="Reload theirs" type="light" onClick={onReloadTheirs} />
        {canOverwrite(reason) && currentVersion != null && (
          <Button title="Overwrite with mine" type="destructive" onClick={onOverwriteMine} />
        )}
      </div>
    </div>
  </DialogWrapper>
)
